export class NumberFormatterAdapter {
  /**
   * Formats the number using the '#,##0' pattern.
   * Rounds to the nearest integer with grouping (e.g., "123,456").
   */
  formatAsInteger(value: number): string {
    return this.formatNumber(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  /**
   * Formats the number using the '#,##0.00' pattern.
   * Rounds to two decimal places with grouping (e.g., "123,456.78").
   */
  formatAsTwoDecimal(value: number): string {
    return this.formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /**
   * Formats the number using the '#,##0.00_);(#,##0.00)' pattern.
   * Rounds to two decimal places with grouping, and wraps negative numbers in parentheses (e.g., "(123,456.78)").
   */
  formatAsAccounting(value: number): string {
    if (value < 0) {
      return `(${this.formatNumber(Math.abs(value), { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`;
    }
    return this.formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /**
   * Private helper to format numbers using Intl.NumberFormat.
   * @param value - The number to format.
   * @param options - Formatting options based on Intl.NumberFormat.
   * @returns Formatted number as a string.
   */
  private formatNumber(value: number, options: Intl.NumberFormatOptions): string {
    const formatter = new Intl.NumberFormat("en-US", options);
    return formatter.format(value);
  }

  parseFormattedNumber(str: string) {
    if (typeof str !== 'string') return null;

    const cleaned = str.replaceAll(/[^0-9.-]/g, "").trim();

    // Handle multiple dots (invalid number)
    if ((cleaned.match(/\./g) || []).length > 1) return null;
    if ((cleaned.match(/-/g) || []).length > 1) return null;
  
    const num = Number.parseFloat(cleaned);
    return Number.isNaN(num) ? null : num;
  }
}
